import { motion, useReducedMotion } from 'framer-motion';
import { CheckCircle, Lock, ChevronDown } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { interpolate } from '../../utils/cabinI18n';

/**
 * Theme stage header card for CabinRoadmap.
 * stage: { id, name_tr, name_en, sub_tr, sub_en, theme_icon, gradient, shadowColor,
 *           progress, completedModules, totalModules, status }
 */
export default function ThemeStageHeader({ stage, isExpanded, onToggle }) {
  const { t, lang } = useLanguage();
  const shouldReduce = useReducedMotion();

  const name = lang === 'tr' ? stage.name_tr : stage.name_en;
  const sub = lang === 'tr' ? stage.sub_tr : stage.sub_en;

  const isCompleted = stage.status === 'completed';
  const isActive = stage.status === 'active';
  const isLocked = stage.status === 'locked';

  const progressPct = stage.progress || 0;


  const borderColor = isCompleted
    ? 'border-green-300'
    : isActive
    ? 'border-transparent'
    : 'border-gray-200';

  const bgColor = isCompleted
    ? 'bg-green-50'
    : isActive
    ? 'bg-white'
    : 'bg-gray-50';

  const labelClass = isCompleted
    ? 'bg-green-100 text-green-700'
    : isActive
    ? 'bg-amber-100 text-amber-700'
    : 'bg-gray-100 text-gray-500';

  const statusLabel = isCompleted
    ? (lang === 'tr' ? 'Tamamlandı' : 'Completed')
    : isActive
    ? (lang === 'tr' ? 'Aktif' : 'Active')
    : (lang === 'tr' ? 'Kilitli' : 'Locked');

  const modulesLabel = interpolate(t('cabin.roadmap.modules_count'), {
    completed: stage.completedModules || 0,
    total: stage.totalModules || 0,
  });

  const progressLabel = lang === 'tr'
    ? `%${progressPct} tamamlandı`
    : `${progressPct}% completed`;

  return (
    <motion.button
      onClick={isLocked ? undefined : onToggle}
      disabled={isLocked}
      className={`
        w-full text-left rounded-2xl border-2 ${borderColor} ${bgColor}
        p-4 transition-all duration-200
        ${isLocked ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:shadow-md'}
        ${isActive ? `shadow-lg` : ''}
        focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]
      `}
      style={
        isActive
          ? { boxShadow: `0 4px 20px -4px ${stage.shadowColor}40, 0 2px 0 0 ${stage.shadowColor}50` }
          : undefined
      }
      aria-expanded={isLocked ? undefined : isExpanded}
      aria-label={`${name} — ${progressLabel}, ${modulesLabel}`}
      whileHover={(!isLocked && !shouldReduce) ? { y: -1 } : {}}
      whileTap={(!isLocked && !shouldReduce) ? { scale: 0.99 } : {}}
      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
    >
      <div className="flex items-center gap-4">
        {/* Theme icon circle */}
        <div
          className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${stage.gradient} flex items-center justify-center shrink-0 shadow-md`}
          aria-hidden="true"
        >
          {isCompleted ? (
            <CheckCircle size={26} className="text-white" />
          ) : isLocked ? (
            <Lock size={22} className="text-white/80" />
          ) : (
            <span className="text-2xl">{stage.theme_icon}</span>
          )}
        </div>

        {/* Text content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h2 className="text-base font-black text-[var(--text-main)]">{name}</h2>
            <span className={`px-2 py-0.5 rounded-lg text-xs font-bold ${labelClass}`}>
              {statusLabel}
            </span>
          </div>
          <p className="text-xs text-[var(--text-muted)] mb-2">{sub}</p>

          {/* Progress bar */}
          {!isLocked && (
            <div className="flex items-center gap-2">
              <div
                className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden"
                role="progressbar"
                aria-valuenow={progressPct}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={progressLabel}
              >
                <div
                  className={`h-full rounded-full bg-gradient-to-r ${stage.gradient} transition-all duration-700`}
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="text-xs font-bold text-[var(--text-muted)] shrink-0 w-10 text-right">
                {progressPct}%
              </span>
            </div>
          )}

          {isLocked && (
            <p className="text-xs text-gray-400">
              {lang === 'tr' ? 'Önceki temayı tamamla' : 'Complete previous theme to unlock'}
            </p>
          )}
        </div>

        {/* Chevron */}
        {!isLocked && (
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: shouldReduce ? 0 : 0.2 }}
            className="shrink-0 text-[var(--text-muted)]"
            aria-hidden="true"
          >
            <ChevronDown size={20} />
          </motion.div>
        )}
      </div>

      {/* Bottom stats row */}
      {!isLocked && (
        <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-100">
          <span className="text-xs text-[var(--text-muted)]">
            {modulesLabel}
          </span>
          {isActive && (
            <span className="ml-auto text-xs font-semibold text-[var(--brand-primary)]">
              {lang === 'tr' ? '▶ Devam et' : '▶ Continue'}
            </span>
          )}
          {isCompleted && (
            <span className="ml-auto text-xs font-semibold text-green-600">
              {lang === 'tr' ? '✓ Tamamlandı' : '✓ All done'}
            </span>
          )}
        </div>
      )}
    </motion.button>
  );
}
